import React, { useCallback, useState } from 'react';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import { api } from '../../app/http-common';
import { useCanEdit } from '../../hooks/useFrontegg';
import useBucketData from '../BucketManagerNew/hooks/useBucketData';
import TargetBucketsPanel from '../BucketManagerNew/components/TargetBucketsPanel';
import UndoToast from '../BucketManagerNew/components/UndoToast';
import { STORY_BUCKET_TYPE } from './BucketSidebar';
import useExclusionConfirm from './useExclusionConfirm';
import ExclusionWarningModal from './ExclusionWarningModal';
import '../BucketManagerNew/BucketManagerNew.css';
import './StoriesBucketPanel.css';

// Adi #1 — the Stories screen kept the ORIGINAL flat bucket sidebar while the
// redesigned Target buckets panel (publisher grouping, search, favourites
// ordering, destination preview) lived only inside Bucket Stories. Same panel,
// same behaviour, now on both screens.
//
// The only thing that differs is what a drop means. Inside a bucket a drop is a
// move/copy between buckets; here the story is not in a bucket yet, so it is an
// add. TargetBucketsPanel delegates the drop to its host, so it needed no
// knowledge of either — only which drag types to accept.
const PANEL_WIDTH = 340;

export default function StoriesBucketPanel({ isOpen, onToggle, onWidthChange, collectionScope }) {
  const canEdit = useCanEdit();
  const { clients, loading, reload } = useBucketData();
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [toast, setToast] = useState(null);
  // This panel is mounted on BOTH the Stories screen and the Articles screen
  // (CreateBriefComponent renders it at steps 0 and 1), so wiring the
  // confirmation here covers both of the screens a story can be added from by
  // hand. One mount, one dialog.
  const exclusion = useExclusionConfirm();

  React.useEffect(() => {
    onWidthChange?.(isOpen ? PANEL_WIDTH : 0);
  }, [isOpen, onWidthChange]);

  const toggleClient = useCallback((clientId, forceOpen = false) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (forceOpen) next.add(clientId);
      else if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }, []);

  const handleDropStory = useCallback(async (dragged, bucket) => {
    const storyId = dragged?.storyId;
    if (!storyId) return;

    // Before anything is written: does this bucket refuse one of the story's
    // languages? Resolves to null when the administrator backs out or the
    // bucket is enforcing on all paths, in which case nothing is attempted.
    const decision = await exclusion.request({
      bucketId: bucket._id, storyId, bucketName: bucket.name,
    });
    if (!decision) return;

    try {
      // Same manual add as the feed row, so it records the same scope.
      await api.addStoryToBucket(bucket._id, storyId, collectionScope, { silent: true }, {
        overrideExclusion: decision.override, screen: 'stories_bucket_panel',
      });
      setToast({ message: `Added to ${bucket.name}.`, type: 'success' });
      reload();
    } catch (err) {
      // 409 is the bucket already holding this story — a normal outcome worth
      // naming plainly rather than reporting as a failure.
      const status = err?.response?.status;
      setToast({
        message: status === 409
          ? `Already in ${bucket.name}.`
          : (err?.response?.data?.error || 'Could not add the story.'),
        type: status === 409 ? 'success' : 'error',
      });
    }
  }, [reload, collectionScope, exclusion]);

  const openBucket = useCallback((bucket) => {
    window.open(`/publisher-buckets-new/bucket/${bucket._id}`, '_blank', 'noopener');
  }, []);

  if (!canEdit) return null;

  return (
    <>
      <button
        className={`bucket-sidebar-toggle ${isOpen ? 'open' : ''}`}
        onClick={onToggle}
        title={isOpen ? 'Hide Publisher Buckets' : 'Show Publisher Buckets'}
        style={isOpen ? { right: PANEL_WIDTH } : undefined}
      >
        <InventoryOutlinedIcon style={{ fontSize: 18 }} />
      </button>
      {isOpen && (
        <div className="pbnew stories-bucket-panel">
          <TargetBucketsPanel
            clients={clients}
            loading={loading}
            activeBucketId={null}
            collapsed={false}
            onToggleCollapsed={onToggle}
            onDropStory={handleDropStory}
            onOpenBucket={openBucket}
            expandedIds={expandedIds}
            onToggleClient={toggleClient}
            acceptTypes={STORY_BUCKET_TYPE}
            dropHint="Drop a story onto a bucket to add it."
            title="Publisher Buckets"
          />
        </div>
      )}
      {/* The toast lives outside the .pbnew wrapper above (it must survive the
          panel being closed), so it needs its own token scope or var(--navy)
          resolves to nothing and the pill renders transparent. */}
      <div className="pbnew-scope">
        <UndoToast toast={toast} onDismiss={() => setToast(null)} />
      </div>
      <ExclusionWarningModal {...exclusion.modalProps} />
    </>
  );
}
